import { JobPostings } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { JobPosting } from '@platform/client/schemas';
import { useCallback, useMemo, useState, useContext } from 'react';
import { CreateJobPostingModal } from './CreateJobPostingModal';
import { DeleteJobPostingModal } from './DeleteJobPostingModal';
import { UpdateJobPostingModal } from './UpdateJobPostingModal';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';

const JobPostingsAdmin = () => {
  return (
    <AdminListPageWrapper>
      <JobPostingsAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<JobPosting>();

const JobPostingsAdminContent = () => {
  const { adminQueryOptions, setPagination } = useContext(AdminTableContext);
  const [isCreateJobPostingOpen, setIsCreateJobPostingOpen] = useState(false);
  const [openJobPosting, setOpenJobPosting] = useState<JobPosting | undefined>();
  const [jobPostingToDelete, setJobPostingToDelete] = useState<number | undefined>(undefined);

  const { data: jobPostingCount = adminQueryOptions.page } = JobPostings.useCountJobPostingsAdmin();

  const {
    data: jobPostings = [],
    isLoading,
    isError,
    isSuccess,
  } = JobPostings.useGetJobPostingsAdmin(
    {
      page: adminQueryOptions.page,
      limit: adminQueryOptions.limit,
      sort: adminQueryOptions.sort,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const openJobPostingModal = useCallback(
    (jobPostingId: number) => {
      setOpenJobPosting(jobPostings.find((jobPosting) => jobPosting.id === jobPostingId));
    },
    [jobPostings],
  );

  const openDeleteJobPostingModal = (jobPostingId: number) => {
    setJobPostingToDelete(jobPostingId);
  };

  const closeJobPostingModal = () => {
    setOpenJobPosting(undefined);
    setIsCreateJobPostingOpen(false);
  };

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          cell: (info) => info.getValue(),
          header: () => <span>Id</span>,
          size: 50,
          enableSorting: true,
        }),
        columnHelper.accessor('title', {
          cell: (info) => info.getValue(),
          header: () => <span>Title</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('description', {
          cell: (description) => {
            let descriptionText = description.getValue();
            if (descriptionText.length > 150) {
              descriptionText = description.getValue().substring(0, 150) + '...';
            }
            return descriptionText;
          },
          header: () => 'Description',
          size: 350,
          maxSize: 500,
          enableSorting: false,
        }),
        columnHelper.accessor('date', {
          cell: (date) => new Date(date.getValue()).toLocaleDateString(),
          header: () => <span>Date</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('location', {
          cell: (location) => location.getValue(),
          header: () => <span>Location</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('url', {
          cell: (info) => <div className='text-wrap w-[150px] break-words'>{info.getValue()}</div>,
          header: () => <span>Url</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('createdAt', {
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          header: () => <span>Created At</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.display({
          id: 'Actions',
          header: () => <span>Actions</span>,
          size: 25,
          cell: ({ row }) => (
            <AdminActionsContainer
              actions={[
                {
                  type: 'button',
                  text: 'Edit',
                  onPress: () => openJobPostingModal(row.getValue('id')),
                },
                {
                  type: 'button',
                  text: 'Delete',
                  onPress: () => openDeleteJobPostingModal(row.getValue('id')),
                  linkColor: 'red',
                },
              ]}
            />
          ),
        }),
      ] as ColumnDef<JobPosting, unknown>[],
    [openJobPostingModal],
  );

  return (
    <>
      <AdminTable
        data={jobPostings}
        columns={columns}
        filteredCount={jobPostingCount}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
        tableActions={[
          {
            callback: () => setIsCreateJobPostingOpen(true),
            text: 'Create job posting',
          },
        ]}
      />
      <CreateJobPostingModal
        isOpen={isCreateJobPostingOpen}
        onClose={closeJobPostingModal}
        resetPagination={() =>
          setPagination({ pageIndex: adminQueryOptions.page, pageSize: adminQueryOptions.limit })
        }
        limit={adminQueryOptions.limit}
      />
      {jobPostingToDelete && (
        <DeleteJobPostingModal
          isOpen={!!jobPostingToDelete}
          onClose={() => setJobPostingToDelete(undefined)}
          id={jobPostingToDelete}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
      {openJobPosting && (
        <UpdateJobPostingModal
          jobPosting={openJobPosting}
          isOpen={!!openJobPosting}
          onClose={closeJobPostingModal}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
    </>
  );
};

export default JobPostingsAdmin;
